import { useEffect, useRef, useState } from "preact/hooks";
import { Ban, Share2, ThumbsDown, ThumbsUp, X } from "lucide-preact";

import { articleDetailPath, fetchJson } from "../api/fetch";
import { getArticle, upsertArticleDetail } from "../store/db";
import { pushBodyScrollLock } from "../util/bodyScrollLock";
import type { EngagementType } from "./engagement";
import type { ArticleDetail, ManifestEntry } from "./types";

/** Action-row icon size, matching ArticleCard. */
const ACTION_ICON = 18;

/** Slide-in/out animation duration in ms. Mirrors the CSS `transition:
 *  transform 240ms ease` on `.story-pane` — the JS unmount delay is set to
 *  this plus a small buffer so the pane isn't yanked mid-transition. */
const SLIDE_DURATION_MS = 240;
const UNMOUNT_DELAY_MS = SLIDE_DURATION_MS + 20;

/** Generate a unique marker for `history.state.articleDetail`. Distinct per
 *  mount so a stray `articleDetail: true` pushed by future code from another
 *  surface can't collide with this instance's marker on cleanup. */
let nextMarkerId = 0;
function freshMarker(): number {
  nextMarkerId += 1;
  return nextMarkerId;
}

export interface ArticleDetailProps {
  entry: ManifestEntry;
  onClose: () => void;
  /** Engagement recorder — fires impression/click/dwell/like/share so the
   * EngagementBuffer can flush them to the worker. Independent of the
   * local UI mutators below: those handle the optimistic-feedback state
   * even when no worker is reachable. */
  onEngage?: (type: EngagementType, entry: ManifestEntry) => void;
  /** Toggle this article's liked state in the Feed-level set. The button
   * relabels "♥ Like" → "♥ Liked" without waiting for the network. */
  onLike?: (entry: ManifestEntry) => void;
  /** Trigger Web Share API / clipboard fallback. The Feed-level handler
   * fires the engagement and runs the actual share. */
  onShare?: (entry: ManifestEntry) => void;
  /** Hide this article (thumbs-down). Filters from the feed AND fires
   *  the `hide` engagement which nudges the article's tags down by
   *  −1.0 in the local interest vector. Closes the slide-out. */
  onHide?: (entry: ManifestEntry) => void;
  /** Whether the current article is in the liked set. */
  liked?: boolean;
  /** Pre-computed related stories (tag-overlap, top N) to render under
   *  the article body. Empty / undefined means no rail. The parent
   *  computes these so the slide-out doesn't need to know about the
   *  full manifest. */
  related?: readonly ManifestEntry[];
  /** Open another article in the same slide-out (related-rail tap).
   *  The same `ArticleDetailView` instance re-renders with the new
   *  `entry`; the swap effect resets body scroll, refocuses the close
   *  button, clears the loaded-detail state, and lets the data-loading
   *  + dwell effects re-run via their `[entry.id]` deps. When undefined
   *  the rail is read-only — tapping a card does nothing.
   *
   *  History semantics: a related-tap does NOT push a fresh history
   *  entry. Each swap "replaces the current view" rather than
   *  "navigates forward" — the back gesture from a swapped-in article
   *  closes the pane rather than stepping back to the previously-open
   *  article. Picked deliberately to keep the in-app convention "back
   *  from a slide-out goes to the feed" consistent; if you change it,
   *  also update the history-marker effect to push on swap (and pop
   *  the prior swap-marker so the chain is balanced). */
  onSelectRelated?: (entry: ManifestEntry) => void;
  /** Persist the current article's publisher to the per-device block
   *  list and close the slide-out. When undefined or when the article
   *  has no publisher, the block link is hidden. */
  onBlockPublisher?: (entry: ManifestEntry) => void;
}

type State =
  | { kind: "loading" }
  | { kind: "loaded"; detail: Partial<ArticleDetail> & ManifestEntry }
  | { kind: "error"; message: string };

export function ArticleDetailView({
  entry,
  onClose,
  onEngage,
  onLike,
  onShare,
  onHide,
  onBlockPublisher,
  liked,
  related,
  onSelectRelated,
}: ArticleDetailProps) {
  const [state, setState] = useState<State>({ kind: "loading" });
  // Whether the block-publisher button is armed (first tap). Blocking
  // hides every story from the publisher, so it takes a confirm tap. Reset
  // on close/reopen AND on a related-story swap (the [entry.id] effect).
  const [blockArmed, setBlockArmed] = useState(false);
  const confirmBlockRef = useRef<HTMLButtonElement | null>(null);
  // Drives the CSS slide-in. Mounted as `translateX(100%)` then flipped to
  // `translateX(0)` on the next animation frame so the transition fires.
  // Without the rAF the pane jumps in instantly because the initial paint
  // already lands at the open position.
  const [open, setOpen] = useState(false);
  // The deferred-unmount timer. Stored in a ref so every cleanup path
  // (effect teardown, second close call, popstate fired mid-close) can
  // cancel it — otherwise a rapid close→reopen cycle would let the first
  // pane's setTimeout fire onClose after the second pane mounted.
  const unmountTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  // Ref to the scrollable body container. The swap-in-place effect
  // (below) resets `scrollTop = 0` on `entry.id` change so tapping a
  // related card from a scrolled-down position doesn't open the next
  // article mid-scroll past its hero / title.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // Unique marker for this instance's history.pushState — distinguishes
  // "the back button closed THIS pane" from "the back button closed some
  // future PR's other dialog that happened to use the same key name."
  const historyMarker = useRef<number>(freshMarker());

  // Slide-in on mount, slide-out on close. The close path waits for the
  // transition to finish (matches the CSS duration) before unmounting via
  // the parent's onClose — otherwise the pane vanishes mid-animation.
  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const closeWithAnim = () => {
    setOpen(false);
    // If a previous close-with-anim is still pending, cancel it. The
    // result is the same (a single onClose fires UNMOUNT_DELAY_MS from
    // now) and we don't leak a stale handle if the component
    // re-renders mid-close.
    if (unmountTimer.current !== null) clearTimeout(unmountTimer.current);
    unmountTimer.current = setTimeout(() => {
      unmountTimer.current = null;
      onClose();
    }, UNMOUNT_DELAY_MS);
  };

  // Body scroll-lock while the pane is open. Without this, touch/scroll
  // on the backdrop bubbles up and scrolls the feed underneath (visible
  // on iOS Safari + Brave). Uses the shared refcount module so a
  // simultaneous Settings drawer doesn't leak the lock — see
  // util/bodyScrollLock.ts.
  useEffect(() => pushBodyScrollLock(), []);

  // ESC closes the pane. Captured on the document so focus inside the pane
  // (e.g. on the "Read on publisher" link) doesn't swallow the key.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeWithAnim();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Focus management for the `aria-modal="true"` contract. Remember what
  // was focused before the pane mounted (the card title button, typically),
  // move focus into the pane, and restore on unmount. Without this, Tab
  // can escape backward to the feed underneath and assistive tech doesn't
  // know the modal boundary changed.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Double rAF: the slide-in `setOpen(true)` is queued on rAF #1; we
    // need our focus call to land on rAF #2 so the `.open` class has
    // been applied and the pane is no longer at `translateX(100%)`.
    // Single-rAF was racy — execution order between two same-frame rAFs
    // isn't guaranteed across browsers, and the failure mode is focusing
    // a translated-off element which scrolls the page on some browsers
    // (the exact thing the comment claimed to prevent).
    let inner = 0 as number | null;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        inner = null;
        closeButtonRef.current?.focus();
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner !== null) cancelAnimationFrame(inner);
      // Restore focus only if the originating element is still attached
      // to the document. If the feed re-rendered or the manifest
      // refreshed during the open lifetime, the previously-focused
      // element is detached and `.focus()` silently no-ops to `<body>`;
      // catch that and skip to avoid an invisible focus drop.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus?.();
      }
    };
  }, []);

  // Swap-in-place effect: when the parent passes a different `entry`
  // (related-rail tap), reset the body scroll position to the top,
  // move focus back to the close button, AND reset the loaded-detail
  // state. Without the state reset, the data-loading effect (which
  // only flips its `cancelled` flag on dep change, never re-seeds)
  // leaves A's `state.detail.dek` / `summary` / `byline` rendered
  // under B's title + hero until B's `getArticle` / `fetchJson`
  // resolves — cache-hit case is one frame, slow-network case is
  // hundreds of ms. Either way it's wrong content under a correct
  // title.
  //
  // Skips the very first render (the mount effects already focused
  // and `state` is initialised to `{ kind: "loading" }` by useState).
  // A small ref tracks "have we run for at least one entry id" so the
  // second `entry.id` is the first swap that does work here.
  const seenInitialEntry = useRef(false);
  useEffect(() => {
    if (!seenInitialEntry.current) {
      seenInitialEntry.current = true;
      return;
    }
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    closeButtonRef.current?.focus();
    // A related-story swap re-renders this instance with a new entry
    // without unmounting, so disarm the block guard — otherwise the new
    // publisher would be one tap from being blocked.
    setBlockArmed(false);
    setState({ kind: "loading" });
  }, [entry.id]);

  // Move focus to the confirm button when the block arms, so keyboard/AT
  // users perceive the new step (the pane is otherwise careful with focus).
  useEffect(() => {
    if (blockArmed) confirmBlockRef.current?.focus();
  }, [blockArmed]);

  // Browser back gesture (Android swipe, iOS edge-swipe in PWA mode) closes
  // the pane instead of navigating away. Push a unique-marker state on open,
  // listen for popstate to close (matching only OUR marker), and pop our
  // marker on programmatic close so the history stack doesn't grow with
  // stray entries when the pane is dismissed by tapping the X or backdrop.
  useEffect(() => {
    const marker = historyMarker.current;
    history.pushState({ articleDetail: marker }, "");
    const onPop = () => {
      // Marker already popped — animate out, then call onClose without
      // re-popping. Reuse the timer ref so a parallel close path can
      // cancel this animation too.
      setOpen(false);
      if (unmountTimer.current !== null) clearTimeout(unmountTimer.current);
      unmountTimer.current = setTimeout(() => {
        unmountTimer.current = null;
        onClose();
      }, UNMOUNT_DELAY_MS);
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // If unmount was triggered by something OTHER than the back gesture
      // (X button, backdrop tap, ESC), OUR marker is still on the stack.
      // Pop it so the user's next "back" doesn't no-op. Checking equality
      // (not truthiness) prevents popping a marker pushed by some other
      // surface.
      if (history.state?.articleDetail === marker) history.back();
      // Final safety net: if a deferred-unmount timer is still pending
      // when we unmount for any reason, cancel it so it can't fire
      // post-unmount and run onClose against a stale parent.
      if (unmountTimer.current !== null) {
        clearTimeout(unmountTimer.current);
        unmountTimer.current = null;
      }
    };
  }, []);

  // Dwell timers: fire dwell_30s / dwell_120s if the detail stays open that
  // long, then clear on close so a quick bounce records neither.
  useEffect(() => {
    if (!onEngage) return;
    const t30 = setTimeout(() => onEngage("dwell_30s", entry), 30_000);
    const t120 = setTimeout(() => onEngage("dwell_120s", entry), 120_000);
    return () => {
      clearTimeout(t30);
      clearTimeout(t120);
    };
  }, [entry.id, onEngage]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1. IndexedDB first — instant if cached.
      const cached = await getArticle(entry.id);
      if (cached && cached.summary && !cancelled) {
        setState({ kind: "loaded", detail: cached as ArticleDetail });
      }
      // 2. Network (the SW's NetworkFirst caches this). Updates in place
      //    when fresh.
      try {
        const fresh = await fetchJson<ArticleDetail>(articleDetailPath(entry.id));
        if (cancelled) return;
        await upsertArticleDetail(fresh);
        setState({ kind: "loaded", detail: fresh });
      } catch (err) {
        if (cancelled) return;
        if (!cached) {
          setState({ kind: "error", message: (err as Error).message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entry.id]);

  return (
    <div
      class={open ? "story-backdrop open" : "story-backdrop"}
      onClick={closeWithAnim}
    >
      <aside
        class={open ? "story-pane open" : "story-pane"}
        role="dialog"
        aria-modal="true"
        aria-labelledby="article-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="story-header">
          <button
            class="story-close"
            aria-label="Close"
            onClick={closeWithAnim}
            ref={closeButtonRef}
          >
            <X size={22} aria-hidden="true" />
          </button>
          {entry.publisher ? (
            <span class="story-publisher">{entry.publisher}</span>
          ) : null}
        </header>
        <div class="story-body" ref={bodyRef}>
          {entry.image_url ? (
            <img
              class="story-hero"
              src={entry.image_url}
              alt=""
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : null}
          <h2 id="article-title" class="story-title">
            {entry.title}
          </h2>
          {/*
            Summary preference:
              1. loaded detail.summary (LLM-generated, ≤30 words, freshest)
              2. loaded detail.dek (publisher-authored teaser)
              3. manifest entry.summary (same as #1 but shipped on the
                 manifest — present from the moment the pane opens)
            The manifest summary is shown immediately so the pane never
            renders a bare "Loading…" stub even when the per-article
            detail fetch is slow or 404s. `.trim()` guards against an
            empty-string detail.summary stomping a non-empty manifest
            summary — `??`-only would short-circuit on null/undefined
            only and let "" through.
          */}
          {state.kind === "loaded" && state.detail.dek ? (
            <p class="dek">{state.detail.dek}</p>
          ) : null}
          {(() => {
            const loaded =
              state.kind === "loaded" ? state.detail.summary?.trim() : "";
            const summary =
              loaded && loaded.length > 0 ? loaded : entry.summary;
            return summary ? <p class="summary">{summary}</p> : null;
          })()}
          {state.kind === "loaded" && state.detail.byline ? (
            <p class="byline">{state.detail.byline}</p>
          ) : null}
          {state.kind === "error" ? (
            <p class="error">
              Couldn't load extended details: {state.message}
            </p>
          ) : null}
          <a
            class="cta"
            href={entry.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Read on {entry.publisher ?? "publisher"} →
          </a>
          {onBlockPublisher && entry.publisher ? (
            blockArmed ? (
              // Second step: explicit confirm + cancel so a block is never
              // a single accidental tap. Blocking hides every story from
              // this publisher, so it's worth the extra push.
              <div class="block-confirm">
                <button
                  class="block-publisher block-confirm-yes"
                  ref={confirmBlockRef}
                  onClick={() => {
                    onBlockPublisher(entry);
                    setBlockArmed(false);
                  }}
                >
                  <Ban size={14} aria-hidden="true" />
                  Confirm: block {entry.publisher}
                </button>
                <button
                  class="block-confirm-cancel"
                  onClick={() => setBlockArmed(false)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                class="block-publisher"
                onClick={() => setBlockArmed(true)}
              >
                {/* No aria-label: the visible text is self-describing, and
                  * the Ban icon is aria-hidden, so AT announces exactly the
                  * label sighted users read ("Block all from <publisher>"). */}
                <Ban size={14} aria-hidden="true" />
                Block all from {entry.publisher}
              </button>
            )
          ) : null}
          {onEngage || onLike || onHide || onShare ? (
            <div class="detail-actions">
              {/* Thumbs up = `like`: +1.0 on tags in the local interest
                * vector. The `aria-pressed` + accent border are the
                * persistent "on" indicator after a tap. */}
              <button
                class={liked ? "action thumbs-up active" : "action thumbs-up"}
                aria-pressed={liked ? "true" : "false"}
                aria-label={liked ? "Thumbs up (on)" : "Thumbs up"}
                onClick={() => {
                  // Fire `like` only on the false→true transition. The
                  // engagement vocab in engagement.ts has no `unlike`, so
                  // re-firing on toggle-off would double-count signal into
                  // rank / personas / analytics. Toggle the local UI state
                  // unconditionally; recorder gets just the like.
                  if (!liked) onEngage?.("like", entry);
                  onLike?.(entry);
                }}
              >
                <ThumbsUp size={ACTION_ICON} aria-hidden="true" />
              </button>
              {/* Thumbs down = `hide`: -1.0 on tags AND removes from feed.
                * Closing the slide-out is handled inside Feed.tsx's `hide`
                * (it sets selected=null when the hidden article is the
                * one open). */}
              {onHide ? (
                <button
                  class="action thumbs-down"
                  aria-label="Thumbs down"
                  onClick={() => {
                    onHide(entry);
                    onEngage?.("hide", entry);
                  }}
                >
                  <ThumbsDown size={ACTION_ICON} aria-hidden="true" />
                </button>
              ) : null}
              <button
                class="action share"
                aria-label="Share"
                onClick={() => {
                  onShare?.(entry);
                  onEngage?.("share", entry);
                }}
              >
                <Share2 size={ACTION_ICON} aria-hidden="true" />
              </button>
            </div>
          ) : null}

          {related && related.length > 0 ? (
            <section
              class="story-related"
              aria-labelledby={`related-${entry.id}`}
            >
              <h3 id={`related-${entry.id}`}>Related stories</h3>
              <ul class="related-list">
                {related.map((r) => (
                  <li key={r.id}>
                    <button
                      class="related-card"
                      onClick={() => onSelectRelated?.(r)}
                      disabled={!onSelectRelated}
                      aria-label={`Open related story: ${r.title}`}
                    >
                      {r.image_url ? (
                        <img
                          class="related-thumb"
                          src={r.image_url}
                          alt=""
                          loading="lazy"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      ) : null}
                      <div class="related-meta">
                        <div class="related-title">{r.title}</div>
                        {r.publisher ? (
                          <div class="related-publisher">{r.publisher}</div>
                        ) : null}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </aside>
    </div>
  );
}
